import { DatePipe, NgClass, NgStyle } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    OnInit,
    output,
    signal,
    untracked,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatAnchor, MatButton, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';

import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { lastValueFrom } from 'rxjs';

import { BaasAppState, BaasI18n, BarGvkManager, openUrl } from '@lvflanders/bar';
import {
    BaasActionButtonCellRenderer,
    BAR_AG_GRID_LOCALE,
    BarAgGridGvk,
    BarDateStringCellRenderer,
    BarIconCellRenderer,
    themeLv
} from '@lvflanders/bar-ng-ag-grid';
import { BaasActionButtonOptions, BaasConfirmService, BaasLayoutService, BaasToastMessageService, BarI18nPipe, BarLayoutService } from '@lvflanders/bar-ng-ui';

import { DidoDocumentenControlApi } from '../../api/dido-documenten-control.api';
import { DIDO_DOCUMENTEN_CONTROL_CONSTANTS } from '../../dido-documenten-control.constants';
import { DidoDocumentenControlColumn } from '../../enums/dido-documenten-control-column.enum';
import { DidoDocumentenControlDisplayModus } from '../../enums/dido-documenten-control-display-modus.enum';
import { DeleteTooltipPipe } from '../../pipes/delete-tooltip.pipe';
import { GetIconClassPipe } from '../../pipes/get-icon-class.pipe';
import { DidoBestandsnaamRenderer } from '../../renderers/dido-bestandsnaam-renderer/dido-bestandsnaam-renderer';
import { SpinnerRenderer } from '../../renderers/spinner-renderer/spinner-renderer';
import { DidoDocumentenService } from '../../services/dido-documenten.service';
import { DidoGalleryService } from '../../services/dido-gallery.service';
import { DocumentModel } from '../../types/dido-document-model.type';
import { DocumentenSaveModel } from '../../types/dido-document-save-model.type';
import { DocumentUploadModel } from '../../types/dido-document-upload-model.type';
import { DidoDocumentenControlOptions } from '../../types/dido-documenten-control-options.type';
import { DidoDocumentenControlParams } from '../../types/dido-documenten-control-params.type';
import { DidoDocumentenRequestModel } from '../../types/dido-documenten-request-model.type';
import { DocumenttypeModel } from '../../types/documenttype-model.type';
import { DidoDocumentenControlDialog } from '../dido-documenten-control-dialog/dido-documenten-control-dialog';
import {
    DidoDocumentenControlDialogDataIntern,
    DidoDocumentenControlDialogInternComponent
} from '../dido-documenten-control-dialog-intern/dido-documenten-control-dialog-intern.component';
import { DidoDocumentenControlUpload } from '../dido-documenten-control-upload/dido-documenten-control-upload';
import { DidoDocumentenGallery } from '../dido-documenten-gallery/dido-documenten-gallery';

@Component({
    selector: 'dido-documenten-control',
    templateUrl: './dido-documenten-control.html',
    styleUrls: ['./dido-documenten-control.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DidoDocumentenControlUpload,
        MatButtonToggleGroup,
        MatButtonToggle,
        MatTooltip,
        MatIcon,
        MatCard,
        MatCardContent,
        MatIconButton,
        NgClass,
        MatButton,
        MatAnchor,
        MatToolbar,
        MatToolbarRow,
        BarAgGridGvk,
        AgGridModule,
        NgStyle,
        BarI18nPipe,
        DeleteTooltipPipe,
        GetIconClassPipe,
        DatePipe
    ]
})
export class DidoDocumentenControl implements OnInit {
    readonly #dialog = inject(MatDialog);
    readonly #i18n = inject(BaasI18n);
    readonly #appState = inject(BaasAppState);
    readonly #confirm = inject(BaasConfirmService);
    readonly #documentenService = inject(DidoDocumentenService);
    readonly #layoutLegacy = inject(BarLayoutService);
    readonly #layout = inject(BaasLayoutService);
    readonly #barToastMessageService = inject(BaasToastMessageService);
    readonly #datePipe = inject(DatePipe);
    readonly #galleryService = inject(DidoGalleryService);
    readonly #barGvkManager = inject(BarGvkManager);
    readonly #deleteTooltipPipe = inject(DeleteTooltipPipe);

    /** The upload component */
    readonly upload = viewChild(DidoDocumentenControlUpload);
    /** Defines whether the documenten control is disabled (which means the documenten control is frozen and everything appears as disabled) */
    readonly disabled = input(false);
    /** Object containing all the options */
    readonly _options = input.required<DidoDocumentenControlOptions>({ alias: 'options' });
    /** Callback which is invoked before saving. */
    readonly preSave = input<(() => Promise<void>) | undefined>(undefined);
    /** Defines whether the documenten control is readonly (which means you can still swap to see everything, but update/delete/insert is not working) */
    readonly readonly = input(false);
    /** Occurs when the documenten control is ready */
    readonly ready = output<DidoDocumentenControlParams>();
    readonly beforeSave = output<void>();
    readonly afterSave = output<number[]>();
    /** used to call save when extern*/
    readonly documentAdded = output<DocumentModel[]>();
    readonly documentDeleted = output<DocumentModel[]>();
    readonly documentEdited = output<DocumentModel[]>();
    readonly documentSelectedOrClicked = output<DocumentModel>();
    // the documenten we are working with (these are also send to the backend including deleted once)
    readonly documenten = signal<DocumentModel[]>([]);
    // for the grid without the deleted items
    protected readonly gridDocumenten = signal<DocumentModel[]>([]);
    // whether in grid modus or list modus
    protected readonly gridModus = signal(false);
    protected readonly gridHeight = signal<string>('');
    protected readonly errors = signal<{ bestandsnaam: string; message: string }[]>([]);

    protected readonly storageKey = computed(() => {
        return `${DIDO_DOCUMENTEN_CONTROL_CONSTANTS.STORAGE_KEY_PREFIX}.gvk-${this.#appState.basiscontext.zone}-${this.#appState.toepassing.code}-${this.options().gvkKeySuffix}`;
    });

    protected readonly options = computed(() => {
        const defaultOptions = {
            extDocVerwijderenToegelaten: true,
            includeVersnipperdeDocumenten: true,
            showUploadButton: true,
            allowUploadMultipleDocuments: true
        } as DidoDocumentenControlOptions;
        if (this._options()) {
            return { ...defaultOptions, ...this._options() };
        }
        return defaultOptions;
    });
    // whether intern or extern
    protected intern = false;

    // options for the ag-grid
    protected readonly gridOptions: GridOptions = {
        defaultColDef: {
            sortable: true,
            resizable: true
        },
        rowSelection: { mode: 'singleRow' },
        localeText: BAR_AG_GRID_LOCALE,
        theme: themeLv,
        // suppressContextMenu: true,
        enableCellTextSelection: true,
        rowClassRules: {
            'dido-documenten_niet-bewaard': (params) => params.data.documentnummer == null
        }
    };

    protected gridApi: GridApi;

    // api used by consumer to talk to documenten control
    #api: DidoDocumentenControlApi;
    // the original documenten from the first request (so we can always undo back to it)
    #originalDocumenten: DocumentModel[];
    // these are the selectable document types appearing in the select list of the dialog -> we retrieve this only once
    #documenttypes: DocumenttypeModel[];
    // prevent the auto swap to grid view
    #preventAutoSwap = false;

    readonly #rowNumberForGrid = computed(() => {
        return this.options()?.gridRowBoundary || 10;
    });

    // whether dirty
    readonly #dirty = computed(() => {
        // dirty if at least one document is dirty
        return this.documenten() != null && this.documenten().some((document) => document.dirty || document.deleted);
    });

    readonly #valid = computed(() => this.errors().length == 0);

    #colDefEditAction: ColDef;
    #colDefDeleteAction: ColDef;
    // current dialog reference or null if no gallery open
    #dialogRefGallery: MatDialogRef<DidoDocumentenGallery> | undefined;

    constructor() {
        // options effect
        effect(() => {
            this.options();
            untracked(async () => {
                this.errors.set([]);
                await this.#loadData();
                await this.#initGrid();
                await this.#defineWeergave();
            });
        });
        // readonly effect
        effect(() => {
            const readonly = this.readonly();
            untracked(() => {
                if (this.gridOptions.columnDefs && this.gridApi) {
                    // hide/show action buttons in case readonly
                    if (!this.intern) {
                        this.gridApi.setColumnsVisible([this.#colDefEditAction.colId as string, this.#colDefDeleteAction.colId as string], !readonly);
                    } else {
                        this.gridApi.setColumnsVisible([this.#colDefEditAction.colId as string], true);
                        this.gridApi.setColumnsVisible([this.#colDefDeleteAction.colId as string], !readonly);
                    }
                    this.gridApi.sizeColumnsToFit();
                }
            });
        });
    }

    async ngOnInit(): Promise<void> {
        this.intern = this.#appState.basiscontext.zone == 'I';
        // define the api for the consumer to communicate
        this.#defineApi();
        // initialize the ag-grid
        await this.#initGrid();
        // define the weergave
        await this.#defineWeergave();
        // emit the params and pass the api to the consumer (we learned this from ag grid for Angular)
        this.ready.emit({ api: this.#api });
    }

    /**
     * Invoked by the grid on ready.
     * @param params
     */
    protected onGridReady(params) {
        this.gridApi = params.api;
        this.gridApi.setGridOption('domLayout', this.documenten() && this.documenten().length > 10 ? 'normal' : 'autoHeight');
        this.gridHeight.set(this.documenten() && this.documenten().length > 10 ? '350px' : '');
        // hide/show action buttons in case readonly
        if (!this.intern) {
            this.gridApi.setColumnsVisible([this.#colDefEditAction.colId as string, this.#colDefDeleteAction.colId as string], !this.readonly());
        } else {
            this.gridApi.setColumnsVisible([this.#colDefEditAction.colId as string], true);
            this.gridApi.setColumnsVisible([this.#colDefDeleteAction.colId as string], !this.readonly());
        }
        this.gridApi.sizeColumnsToFit();
    }

    /**
     * Invoked when the size of the grid changes.
     * @param params
     */
    protected onGridSizeChanged(params) {
        this.gridApi.sizeColumnsToFit();
    }

    protected onUploadError(errors: string[]) {
        this.errors.set(
            errors.map((e) => {
                return { bestandsnaam: '', message: e };
            })
        );
    }

    /**
     * Happens when 1 or more files have been uploaded successfully by the file upload component.
     * @param response
     */
    protected async onUploaded(responses: DocumentUploadModel[]): Promise<void> {
        responses.forEach((response) => this.#documentenService.enrichDocument(response));
        const matDialogConfig: MatDialogConfig = {
            // pas the data needed
            data: {
                documenten: responses,
                documenttypes: this.#documenttypes,
                maxFileSize: this.options().maxFileSize,
                // insert modus
                werktInDossierContext: this.options().dossiernummer && this.options().toepassingCode ? true : false
            } as DidoDocumentenControlDialogDataIntern,
            panelClass: 'dido-documenten__dialog-panel'
        };

        // the dialog handles the rest
        let dialogRef: MatDialogRef<any>;
        if (this.intern) {
            dialogRef = this.#dialog.open(DidoDocumentenControlDialogInternComponent, matDialogConfig);
        } else {
            dialogRef = this.#dialog.open(DidoDocumentenControlDialog, matDialogConfig);
        }

        const data = await lastValueFrom(dialogRef.afterClosed());
        if (data != null && data.length > 0) {
            await this.#handleAddDocumenten(data);
        }
    }

    protected onRowSelectionChanged() {
        const selectedRows = this.gridApi.getSelectedRows();
        if (selectedRows.length === 1) {
            this.documentSelectedOrClicked.emit(selectedRows[0]);
        }
    }

    protected onRowDoubleClicked() {
        if (this.intern) {
            const selectedRows = this.gridApi.getSelectedRows();
            if (selectedRows.length === 1) {
                const documenttoedit = selectedRows[0];
                openUrl(documenttoedit.ori, '_blank');
            }
        }
    }

    protected onDocumentCardClicked(document) {
        this.documentSelectedOrClicked.emit(document);
    }

    protected deleteError(error) {
        this.errors.update((errors) => errors.filter((e) => e.bestandsnaam !== error.bestandsnaam));
    }

    /**
     * Edits the document by opening a dialog.
     * @param document
     */
    protected async edit(document: DocumentModel) {
        const matDialogConfig: MatDialogConfig = {
            panelClass: 'dido-documenten__dialog-panel',

            // pas the data needed
            data: {
                documenten: [document],
                documenttypes: this.#documenttypes,
                // update modus
                update: true
            }
        };

        // the dialog handles the rest
        let dialogRef: MatDialogRef<any>;
        if (this.intern) {
            dialogRef = this.#dialog.open(DidoDocumentenControlDialogInternComponent, matDialogConfig);
        } else {
            dialogRef = this.#dialog.open(DidoDocumentenControlDialog, matDialogConfig);
        }

        const data = await lastValueFrom(dialogRef.afterClosed());

        if (data != null && data.length > 0) {
            this.#handleEditDocument(document, data[0]);
            await this.#refreshUI();
        }
    }

    /**
     * Deletes a document.
     * @param document Document form group for soft delete (in case persisted..
     */
    protected async delete(document: DocumentModel) {
        let message: string;
        const bestandsnaam = document.bestandsnaam.length > 30 ? document.bestandsnaam.substring(0, 29) + '...' : document.bestandsnaam;

        if (document.documentnummer != null) {
            if (this.intern && this.options().versie) {
                message = `Wil je dit document  ${bestandsnaam} verwijderen uit het dossier met versie ${this.options().versie} én alle daaropvolgende versies? `;
            } else {
                message = `Wil je dit document ${bestandsnaam} uit het dossier verwijderen?`;
            }
        } else message = `Bent u zeker dat u ${document.bestandsnaam} wilt verwijderen?`;

        // open a confirmation dialog
        const confirm = await lastValueFrom(
            this.#confirm.open({
                width: '30vw',
                message: message,
                title: this.#i18n.get('DOMAIN_DIDO.COMMON.ACTION.DELETE')
            })
        );

        if (confirm) {
            if (document.documentnummer == null) {
                this.documenten.update((documenten) => {
                    const index = documenten.indexOf(document);
                    documenten.splice(index, 1);
                    // if not existing just remove it form list -> no need to send to back-end
                    return [...documenten];
                });

                await this.#refreshUI();
            } else {
                this.documenten.update((documenten) => {
                    const index = documenten.findIndex((x) => x.documentnummer == document.documentnummer);
                    if (index != -1) documenten[index].deleted = true;
                    // if not existing just remove it form list -> no need to send to back-end
                    return [...documenten];
                });

                this.documentDeleted.emit(this.documenten());
                if (this.intern) {
                    await this.save();
                }
                await this.#refreshUI();
                // make sure to close the gallery if open
                this.#closeGallery();
            }
        }
    }

    protected async swapMode(event) {
        await this.#swapModus(true, event.value);
    }

    /** Saves the data. */
    protected async save(): Promise<void> {
        this.beforeSave.emit();
        const submitModel = {} as DocumentenSaveModel;

        submitModel.dossiernummer = this.options().dossiernummer;
        submitModel.toepassingCode = this.options().toepassingCode;
        submitModel.versie = this.options().versie;
        submitModel.statusExtern = this.options().statusExtern;
        submitModel.explicieteKlanten = this.options().explicieteKlanten;
        submitModel.filterDocumenttypes = this.options().filterDocumenttypes;
        submitModel.documenttypeOptions = this.options().documenttypeOptions;
        submitModel.documentnummers = this.options().documentnummers && this.options().documentnummers?.map((x) => x.toString());
        if (
            (this.options().dossiernummer && this.options().toepassingCode && this.options().documentnummers) ||
            (!this.options().dossiernummer && !this.options().toepassingCode && !this.options().documentnummers)
        ) {
            this.documenten().map((d) => {
                if (!submitModel.documentnummers) {
                    submitModel.documentnummers = [];
                }
                if (d.documentnummer && submitModel.documentnummers!.includes(d.documentnummer.toString()) == false) {
                    submitModel.documentnummers!.push(d.documentnummer.toString());
                }
            });
        }

        submitModel.oorsprongToepassingscode = this.options().oorsprongToepassingscode
            ? this.options().oorsprongToepassingscode
            : this.#appState.toepassing.code;
        // get all dirty documenten (which also includes deleted items)
        submitModel.documenten = this.documenten().filter((document) => document.deleted || document.dirty);
        const preSave = this.preSave();
        if (preSave) await preSave();

        try {
            const result = await lastValueFrom(this.#documentenService.save(submitModel, this.options().showLoader));
            this.#defineInterceptionEnabled(true);
            if (result.validationMessages) {
                this.#barToastMessageService.addMessage({
                    text: 'Er is iets fout gegaan tijdens het bewaren!',
                    type: 'error'
                });
                this.errors.update((errors) => {
                    const newArray = [...errors];

                    result.validationMessages.forEach((message) => {
                        const bestandsnaam = message.split('#')[0];
                        const msg = message.substring(message.indexOf('#') + 1);
                        newArray.push({ bestandsnaam: bestandsnaam, message: msg });
                    });
                    return newArray;
                });
            }
            this.#originalDocumenten = this.options().includeVersnipperdeDocumenten
                ? result.documenten
                : result.documenten.filter((d) => d.status != 'Versnipperd');

            // clear the array
            await this.#initDocumenten();

            // when everything is done we resolve the promise
            this.afterSave.emit(this.#originalDocumenten.map((x) => x.documentnummer));
        } catch (error) {
            this.#defineInterceptionEnabled(true);

            this.#barToastMessageService.addMessage({
                text: 'Er is iets fout gegaan tijdens het bewaren!',
                type: 'error'
            });
        }
    }

    /** Performs an undo. */
    protected async undo(): Promise<void> {
        // assign the array (with original documenten)
        await this.#assignArray();
    }

    /** opens the gallery */
    protected gallery(didoDocument: any) {
        //dirty fix to get displayed rows
        let rowsToDisplay;
        if (this.gridApi) {
            rowsToDisplay = [];
            this.gridApi.forEachNodeAfterFilterAndSort((node) => {
                rowsToDisplay.push(node);
            });
        }
        if (rowsToDisplay) {
            rowsToDisplay = rowsToDisplay.map((x) => x.data);
        } else {
            rowsToDisplay = this.gridDocumenten();
        }
        //als we hier iets wijzigen aan de showDeletebutton denk ook aan de knoppen in de galerij en de tegelweergave
        const config = {
            // pass the data needed
            id: 'galleryDialog',
            documenten: rowsToDisplay.filter((x: DocumentModel) => x.isReadContentAllowed),
            documenttypes: this.#documenttypes,
            selectedDocument: didoDocument,
            toepassingCode: this.options().toepassingCode,
            dossiernummer: this.options().dossiernummer,
            versie: this.options().versie,
            readonly: this.readonly() || this.disabled(),
            minWidth: '85vw',
            panelClass: 'dido-documenten_gallery',
            showDeleteButton: !this.checkDisableDelete(didoDocument)
        };
        this.#dialogRefGallery = this.#galleryService.open(config);
        (document.getElementsByClassName('cdk-overlay-dark-backdrop')[0] as HTMLElement)
            ? ((document.getElementsByClassName('cdk-overlay-dark-backdrop')[0] as HTMLElement).style.background = 'rgba(0, 0, 0, 0.850)')
            : '';

        this.#dialogRefGallery.componentInstance.onEdit.subscribe(async (documentToEdit) => await this.edit(documentToEdit));
        this.#dialogRefGallery.afterClosed().subscribe(() => {
            this.#dialogRefGallery = undefined;
        });
    }

    //used to override the reset column menu item
    getMainMenuItems(params) {
        const menuItems: any[] = [];
        const itemsToExclude = ['resetColumns'];
        params.defaultItems.forEach((item) => {
            if (itemsToExclude.indexOf(item) < 0) {
                menuItems.push(item);
            }
        });
        menuItems.push({
            name: 'Kolommen resetten',
            action: () => {
                params.columnApi.resetColumnState();
                params.columnApi.setColumnVisible(this.#colDefDeleteAction.colId, !this.readonly());

                params.api.sizeColumnsToFit();
            }
        });
        return menuItems;
    }

    resetFilters() {
        this.gridApi.setFilterModel(null);
        this.gridApi.forEachDetailGridInfo((gridInfo, index) => gridInfo.api!.setFilterModel(null));
        this.#barToastMessageService.addMessage({
            text: this.#i18n.get('SHARED.ACTION.RESET_FILTER_INFO'),
            type: 'success'
        });
    }

    exportExcel() {
        const params = {
            autoConvertFormulas: true,
            columnGroups: true,
            fileName: this.#i18n.get('SHARED.ACTION.EXPORT_EXCEL_FILENAME') + this.#datePipe.transform(Date.now(), 'yyyy-MM-dd_HHmmss'),
            sheetName: this.#i18n.get('SHARED.ACTION.EXPORT_EXCEL_SHEETNAME'),
            processCellCallback: (params) => this.#cellCallback(this, params)
        };
        this.gridApi.exportDataAsExcel(params);
    }

    async downloadAllVisibleDocs() {
        const filteredDocuments = [];
        this.gridApi.forEachNodeAfterFilterAndSort((node) => {
            filteredDocuments.push({ documentnummer: node.data.documentnummer, bestandsnaam: node.data.bestandsnaam });
        });

        if (filteredDocuments.length === 0) {
            this.#barToastMessageService.addMessage({
                text: 'Geen documenten om te downloaden',
                type: 'warning'
            });
            return;
        }

        try {
            const blob = await lastValueFrom(this.#documentenService.downloadAllDocuments(filteredDocuments));
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `documenten_${this.#datePipe.transform(Date.now(), 'yyyy-MM-dd_HHmmss')}.zip`;
            link.click();
            window.URL.revokeObjectURL(url);

            this.#barToastMessageService.addMessage({
                text: `${filteredDocuments.length} document(en) gedownload`,
                type: 'success'
            });
        } catch (error) {
            this.#barToastMessageService.addMessage({
                text: 'Er is een fout opgetreden bij het downloaden van de documenten',
                type: 'error'
            });
        }
    }

    /** Swaps between grid modus and list modus (default). */
    async #swapModus(userHasSwapped: boolean = false, value = !this.gridModus()) {
        this.gridModus.set(value);
        if (!this.gridModus() && userHasSwapped) {
            this.#preventAutoSwap = true;
        }
        if (userHasSwapped) {
            // save this voorkeur
            await this.#saveGvk();
        }
    }

    #closeGallery(): void {
        if (this.#dialogRefGallery != undefined) {
            this.#dialogRefGallery.close();
            this.#dialogRefGallery = undefined;
        }
    }

    /** Loads the data from the backend (documenten & documenttypes). */
    async #loadData(): Promise<void> {
        // request object is based on the given options (but doesn't contain all the options)
        const request: DidoDocumentenRequestModel = {
            dossiernummer: this.options().dossiernummer,
            versie: this.options().versie,
            toepassingCode: this.options().toepassingCode,
            documenttypeOptions: this.options().documenttypeOptions,
            documentnummers: this.options().documentnummers,
            filterDocumenttypes: this.options().filterDocumenttypes
        };
        // get the documenten
        this.#originalDocumenten = [];
        if ((this.options().toepassingCode && this.options().dossiernummer) || this.options().documentnummers?.length > 0) {
            const result = await lastValueFrom(this.#documentenService.getDocumenten(request, this.options().showLoader));
            // keep the originals
            if (result.documenten) {
                this.#originalDocumenten = this.options().includeVersnipperdeDocumenten
                    ? result.documenten
                    : result.documenten.filter((d) => d.status != 'Versnipperd');
            }
        }
        await this.#initDocumenten();
        if (this.gridModus()) {
            if (this.gridApi) {
                this.gridApi.setGridOption('domLayout', this.documenten() && this.documenten().length > 10 ? 'normal' : 'autoHeight');
            }
            this.gridHeight.set(this.documenten() && this.documenten().length > 10 ? '350px' : '');
        }

        // get the document types at initialization (used in dialog)
        this.#documenttypes = await lastValueFrom(this.#documentenService.getDocumenttypes(this.options().documenttypeOptions, this.options().showLoader));
    }

    async #defineGridColumns(): Promise<DidoDocumentenControlColumn[]> {
        // get columns from options first
        let result: DidoDocumentenControlColumn[] | undefined = this.options().columns;
        // put the defaults
        if (result == undefined || result.length == 0) {
            if (this.intern) {
                result = [
                    DidoDocumentenControlColumn.Spinner,
                    DidoDocumentenControlColumn.Zichtbaar,
                    DidoDocumentenControlColumn.Bestandsnaam,
                    DidoDocumentenControlColumn.Onderwerp,
                    DidoDocumentenControlColumn.DocumenttypeCode,
                    DidoDocumentenControlColumn.DocumenttypeNaam,
                    DidoDocumentenControlColumn.DocumenttypeOmschrijving,
                    DidoDocumentenControlColumn.Documentnummer,
                    DidoDocumentenControlColumn.PartijKlantNummers,
                    DidoDocumentenControlColumn.Klanten,
                    DidoDocumentenControlColumn.Dossiers,
                    DidoDocumentenControlColumn.AangemaaktDoor,
                    DidoDocumentenControlColumn.AangemaaktDatum,
                    DidoDocumentenControlColumn.Status,
                    DidoDocumentenControlColumn.GewijzigdDoor,
                    DidoDocumentenControlColumn.Taken,
                    DidoDocumentenControlColumn.DossiersVersies,
                    DidoDocumentenControlColumn.Oorsprong,
                    DidoDocumentenControlColumn.GewijzigdDatum
                ];
            } else {
                result = [
                    DidoDocumentenControlColumn.Bestandsnaam,
                    DidoDocumentenControlColumn.Onderwerp,
                    DidoDocumentenControlColumn.Documentnummer,
                    DidoDocumentenControlColumn.AangemaaktDatum,
                    DidoDocumentenControlColumn.DocumenttypeOmschrijving
                ];
            }
        }

        return result;
    }

    /** Initializes the grid options. */
    async #initGrid() {
        this.gridOptions.suppressDragLeaveHidesColumns = !this.intern;
        this.gridOptions.popupParent = document.querySelector('body');
        this.gridOptions.columnDefs = [];
        this.gridOptions.tooltipShowDelay = 100;
        this.gridOptions.pagination = true;
        this.gridOptions.paginationPageSize = 20;
        // indien extern geen menu en ook geen veldenkiezer
        (this.gridOptions.defaultColDef as ColDef).suppressHeaderMenuButton = !this.intern;
        const cols = await this.#defineGridColumns();
        cols.forEach((column) => {
            switch (column) {
                case DidoDocumentenControlColumn.Spinner: {
                    this.gridOptions.columnDefs!.push({
                        colId: DidoDocumentenControlColumn.Spinner.toString(),
                        maxWidth: 38,
                        lockPosition: 'left',
                        pinned: 'left',
                        lockPinned: true,
                        lockVisible: true,
                        suppressHeaderContextMenu: true,
                        suppressColumnsToolPanel: true,
                        suppressMovable: true,
                        suppressFiltersToolPanel: true,
                        cellRenderer: SpinnerRenderer,
                        cellRendererParams: (params) => {
                            return { documentnummer: params.data.documentnummer };
                        }
                    });
                    break;
                }
                case DidoDocumentenControlColumn.Zichtbaar: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.ZICHTBAARHEID'),
                        field: 'zichtbaar',
                        colId: DidoDocumentenControlColumn.Zichtbaar.toString(),
                        minWidth: 65,
                        maxWidth: 65,
                        cellStyle: { 'text-align': 'center' },
                        cellRenderer: BarIconCellRenderer,
                        cellRendererParams: {
                            // use color info to make it blue(dabedee)
                            color: (rowData: DocumentModel) => (rowData.zichtbaar ? 'success' : 'error'),
                            fontSet: 'fas',
                            fontIcon: (rowData: DocumentModel) => (rowData.zichtbaar ? 'fa-eye' : 'fa-eye-slash'),
                            title: (rowData: DocumentModel) =>
                                rowData.zichtbaar
                                    ? this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.EXTERN_ZICHTBAAR')
                                    : this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.NIET_EXTERN_ZICHTBAAR')
                        },
                        filter: 'agNumberColumnFilter',
                        filterParams: {
                            filterOptions: [
                                {
                                    displayKey: 'zichtbaar',
                                    displayName: 'zichtbaar',
                                    predicate: ([filterValue], cellValue) => cellValue === true,
                                    numberOfInputs: 0
                                },

                                {
                                    displayKey: 'nietZichtbaar',
                                    displayName: 'niet zichtbaar',
                                    predicate: ([filterValue], cellValue) => cellValue === false,
                                    numberOfInputs: 0
                                }
                            ],
                            suppressAndOrCondition: true
                        }
                    });
                    break;
                }
                case DidoDocumentenControlColumn.Bestandsnaam: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.BESTANDSNAAM'),
                        field: 'bestandsnaam',
                        filter: true,
                        minWidth: 150,
                        width: 200,
                        colId: DidoDocumentenControlColumn.Bestandsnaam.toString(),
                        cellRenderer: DidoBestandsnaamRenderer,
                        cellRendererParams: {
                            action: (rowData) => {
                                return this.gallery(rowData);
                            }
                        }
                    });
                    break;
                }
                case DidoDocumentenControlColumn.Onderwerp: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.ONDERWERP'),
                        colId: DidoDocumentenControlColumn.Onderwerp.toString(),
                        filter: true,
                        field: 'onderwerp'
                    });
                    break;
                }
                case DidoDocumentenControlColumn.Oorsprong: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.OORSPRONG'),
                        colId: DidoDocumentenControlColumn.Oorsprong.toString(),
                        filter: true,
                        hide: this.options().columns && this.options().columns?.length > 0 ? false : true,
                        field: 'oorsprong'
                    });
                    break;
                }
                case DidoDocumentenControlColumn.GewijzigdDatum: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.GEWIJZIGD_DATUM'),
                        colId: DidoDocumentenControlColumn.GewijzigdDatum.toString(),
                        field: 'gewijzigdDatum',
                        filter: true,
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        tooltipValueGetter: (params) => {
                            if (params.value) {
                                return new Date(params.value).toLocaleString('nl-BE');
                            }
                        },
                        cellRenderer: BarDateStringCellRenderer,
                        cellRendererParams: {
                            format: 'dd/MM/yyyy'
                        }
                    });
                    break;
                }
                case DidoDocumentenControlColumn.DocumenttypeCode: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.DOCUMENTTYPE_CODE'),
                        colId: DidoDocumentenControlColumn.DocumenttypeCode.toString(),
                        filter: true,
                        field: 'documenttypeCode'
                    });
                    break;
                }
                case DidoDocumentenControlColumn.DocumenttypeNaam: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.DOCUMENTTYPE_NAAM'),
                        colId: DidoDocumentenControlColumn.DocumenttypeNaam.toString(),
                        filter: true,
                        field: 'documenttypeNaam'
                    });
                    break;
                }
                case DidoDocumentenControlColumn.DocumenttypeOmschrijving: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.intern
                            ? this.#i18n.get('DOMAIN_DIDO.FIELD.DOCUMENTTYPE_OMSCHRIJVING')
                            : this.#i18n.get('DOMAIN_DIDO.FIELD.DOCUMENTTYPE'),
                        colId: DidoDocumentenControlColumn.DocumenttypeOmschrijving.toString(),
                        filter: true,
                        field: 'documenttypeOmschrijving'
                    });
                    break;
                }
                case DidoDocumentenControlColumn.Documentnummer: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.DOCUMENTNUMMER'),
                        colId: DidoDocumentenControlColumn.Documentnummer.toString(),
                        filter: true,
                        field: 'documentnummerWeergave'
                    });
                    break;
                }
                case DidoDocumentenControlColumn.AangemaaktDatum: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.AANGEMAAKT_DATUM'),
                        colId: DidoDocumentenControlColumn.AangemaaktDatum.toString(),
                        field: 'aangemaaktDatum',
                        filter: true,
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        tooltipValueGetter: (params) => {
                            if (params.value) {
                                return new Date(params.value).toLocaleString('nl-BE');
                            }
                        },
                        cellRenderer: BarDateStringCellRenderer,
                        cellRendererParams: {
                            format: 'dd/MM/yyyy'
                        }
                    });
                    break;
                }

                case DidoDocumentenControlColumn.Klanten: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.KLANTNAAM'),
                        colId: DidoDocumentenControlColumn.Klanten.toString(),
                        filter: true,
                        field: 'klanten',
                        tooltipField: 'klanten',
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        maxWidth: 500
                    });
                    break;
                }
                case DidoDocumentenControlColumn.PartijKlantNummers: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.KLANTEN'),
                        colId: DidoDocumentenControlColumn.PartijKlantNummers.toString(),
                        filter: true,
                        field: 'partijKlantNummers',
                        tooltipField: 'partijKlantNummers',
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        maxWidth: 500
                    });
                    break;
                }

                case DidoDocumentenControlColumn.Dossiers: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.DOSSIERS'),
                        colId: DidoDocumentenControlColumn.Dossiers.toString(),
                        filter: true,
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        field: 'dossiers'
                    });
                    break;
                }
                case DidoDocumentenControlColumn.DossiersVersies: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.DOSSIERVERSIES'),
                        colId: DidoDocumentenControlColumn.DossiersVersies.toString(),
                        filter: true,
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        field: 'dossierVersiesGridString'
                    });
                    break;
                }
                case DidoDocumentenControlColumn.AangemaaktDoor: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.AANGEMAAKTDOOR'),
                        colId: DidoDocumentenControlColumn.AangemaaktDoor.toString(),
                        filter: true,
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        field: 'aangemaaktDoor',
                        tooltipField: 'aangemaaktDoor'
                    });
                    break;
                }

                case DidoDocumentenControlColumn.Taken: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.TAKEN'),
                        colId: DidoDocumentenControlColumn.Taken.toString(),
                        filter: true,
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        field: 'taken'
                    });
                    break;
                }

                case DidoDocumentenControlColumn.Status: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.STATUS'),
                        colId: DidoDocumentenControlColumn.Status.toString(),
                        filter: true,
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        field: 'status'
                    });
                    break;
                }

                case DidoDocumentenControlColumn.GewijzigdDoor: {
                    this.gridOptions.columnDefs!.push({
                        headerName: this.#i18n.get('DOMAIN_DIDO.FIELD.GEWIJZIGD_DOOR'),
                        colId: DidoDocumentenControlColumn.GewijzigdDoor.toString(),
                        filter: true,
                        hide: this.options().columns && this.options().columns.length > 0 ? false : true,
                        field: 'gewijzigdDoor'
                    });
                    break;
                }
            }
        });

        // edit action
        this.#colDefEditAction = {
            colId: 'editOrOri',
            sortable: false,
            filter: false,
            hide: !this.intern && this.readonly(),
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true,
            cellRenderer: BaasActionButtonCellRenderer,
            cellRendererParams: {
                actionButton: (rowData: any) => {
                    //als we hier iets wijzigen denk ook aan de knoppen in de galerij en de tegelweergave

                    // in case already saved (or if extern) we have an edit button, otherwise we have an ori button (intern & saved yet)
                    const hasEdit = !this.intern || rowData.documentnummer == null;

                    const title = this.#i18n.get(hasEdit ? 'DOMAIN_DIDO.COMMON.ACTION.EDIT' : 'DOMAIN_DIDO.DOCUMENTEN_CONTROL.DOCUMENTEN_CENTRUM');
                    const iconClass = hasEdit ? (this.intern ? '' : 'fas fa-pencil-alt') : 'fas fa-share';

                    return <BaasActionButtonOptions>{
                        action: async () => {
                            if (hasEdit) await this.edit(rowData);
                            else openUrl(rowData.ori, '_blank');
                        },
                        icon: {
                            class: iconClass,
                            srText: title,
                            iconOnly: true
                        },
                        title: title,
                        tooltip: title,
                        disabled: computed(() => this.disabled() || (!this.intern && rowData.editDisabled))
                    };
                }
            },
            // autoHeight: true,
            maxWidth: 50,
            minWidth: 50
        };
        this.gridOptions.columnDefs!.push(this.#colDefEditAction);
        // delete action
        this.#colDefDeleteAction = {
            colId: 'delete',
            sortable: false,
            filter: false,
            hide: this.readonly(),
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true,
            cellRenderer: BaasActionButtonCellRenderer,
            cellRendererParams: {
                actionButton: (rowData: DocumentModel) => {
                    //als we hier iets wijzigen denk ook aan de knoppen in de tegelweergave
                    const title = this.#deleteTooltipPipe.transform(rowData, this.options().extDocVerwijderenToegelaten, this.intern);

                    return <BaasActionButtonOptions>{
                        action: async () => await this.delete(rowData),
                        icon: {
                            class: 'fas fa-trash',
                            srText: title,
                            iconOnly: true
                        },
                        tooltip: title,
                        title: title,
                        disabled: computed(() => this.checkDisableDelete(rowData)),
                        hidden:
                            rowData.status == 'Versnipperd' ||
                            rowData.status == 'Vernietigd' ||
                            !(this.options().toepassingCode && this.options().dossiernummer) ||
                            (!this.intern && rowData.aangemaaktDoor && !rowData.aangemaaktDoor.startsWith('RRN\\'))
                    };
                }
            },
            maxWidth: 50,
            minWidth: 50
        };

        this.gridOptions.columnDefs!.push(this.#colDefDeleteAction);
        if (!this.intern) {
            this.gridOptions.columnDefs.forEach((column) => {
                const coldef = column as ColDef;
                if (coldef) {
                    coldef.hide = false;
                }
            });
        }
        if (this.gridApi) {
            this.gridApi.setGridOption('columnDefs', this.gridOptions.columnDefs);
            const gvkCols = await this.#barGvkManager.getItem<any>(this.storageKey() + '-grid');
            if (gvkCols && gvkCols.columnState) {
                this.gridApi.applyColumnState({ state: gvkCols.columnState, applyOrder: true });
            }

            // hide/show action buttons in case readonly
            if (!this.intern) {
                this.gridApi.setColumnsVisible([this.#colDefEditAction.colId as string, this.#colDefDeleteAction.colId as string], !this.readonly());
            } else {
                this.gridApi.setColumnsVisible([this.#colDefEditAction.colId as string], true);
                this.gridApi.setColumnsVisible([this.#colDefDeleteAction.colId as string], !this.readonly());
            }
        }
    }

    /** Initializes the documenten which are fresh from the back-end */
    async #initDocumenten() {
        // make sure to mark everything as non deleted
        this.#originalDocumenten.forEach((document) => {
            document.deleted = false;
            document.dirty = false;
        });
        // assign the form array
        await this.#assignArray();
    }

    /** Defines the api which is an object for the consumer to communicate indirectly with the documenten control. */
    #defineApi(): void {
        this.#api = <DidoDocumentenControlApi>{
            save: () => this.save(),
            undo: () => this.undo(),
            refresh: () => this.#loadData()
        };

        const self = this;
        // make some wrapper properties (goal is that users are discouraged to use AbstractControl directly)
        // users should talk to api or use #documentenControl variable syntax to access values
        Object.defineProperty(this.#api, 'dirty', {
            get() {
                return self.#dirty();
            }
        });
        Object.defineProperty(this.#api, 'valid', {
            get() {
                return self.#valid();
            }
        });
        Object.defineProperty(this.#api, 'errors', {
            get() {
                return self.errors().map((e) => e.message);
            }
        });
    }

    resetUpload(): void {
        if (this.upload()) {
            // reset the upload file requests if present on DOM
            this.upload().reset();
        }
    }
    /** clones the original documenten for the working documenten */
    async #assignArray(): Promise<void> {
        this.documenten.set([]);
        this.#originalDocumenten.forEach((document) => {
            const clone = <DocumentModel>{};
            // clone the original document
            Object.assign(clone, document);
            this.documenten.update((documenten) => [...documenten, clone]);
        });
        await this.#refreshUI();
    }

    /**
     * Occurs after a document is uploaded and the metadata has been defined.
     * This will add the new documenten to the working array and refresh the UI.
     * @param documenten
     */
    async #handleAddDocumenten(documenten: DocumentModel[]) {
        documenten.forEach((document) => {
            // insert it at the front of the array
            this.documenten.update((documenten) => {
                // make new array reference
                const newArray = [...documenten];
                newArray.unshift(document);
                return newArray;
            });
        });
        this.documentAdded.emit(this.documenten());
        await this.#refreshUI();
        if (this.intern) {
            this.#defineInterceptionEnabled(false);
            await this.save();
        }
    }

    /**
     * This occurs when a document has been edited in the dialog and the working document needs to be updated.
     * @param source The original document.
     * @param update The updated document.
     */
    #handleEditDocument(source: DocumentModel, update: DocumentModel): void {
        source.onderwerp = update.onderwerp;
        source.documenttypeCode = update.documenttypeCode;
        source.documenttypeOmschrijving = update.documenttypeOmschrijving;
        source.dirty = true;

        // make sure that signal gets updated
        this.documenten.update((documenten) => [...documenten]);

        this.documentEdited.emit(this.documenten());
    }

    /** This will refresh the user interface without retrieving data. */
    async #refreshUI(): Promise<void> {
        this.#synchronizeGridDocumenten();
        if (this.gridApi != null) {
            // Wrap in setTimeout om AG Grid render conflicts te voorkomen
            setTimeout(() => {
                if (this.gridApi) {
                    this.gridApi.setGridOption('rowData', this.gridDocumenten());
                }
            }, 0);
        }
        if (!this.#preventAutoSwap && this.gridDocumenten().length > this.#rowNumberForGrid() && !this.gridModus()) {
            await this.#swapModus();
        }
    }

    /** This will synchronize with the documenten array for the grid which is different because it doesn't include the deleted documenten. */
    #synchronizeGridDocumenten(): void {
        const gridDocumenten = this.documenten() != null ? this.documenten().filter((document) => !document.deleted) : [];
        gridDocumenten.forEach((document) => {
            if (!document.documentnummer) {
                document.documentnummerWeergave = this.#i18n.get('DOMAIN_DIDO.DOCUMENTEN_CONTROL.NIET_BEWAARD');
            }
        });
        this.gridDocumenten.set(gridDocumenten);
    }

    async #saveGvk(): Promise<void> {
        if (
            !this.readonly() &&
            this.gridApi &&
            !this.gridApi.getColumn(this.#colDefEditAction.colId as string)?.isVisible() &&
            !this.gridApi.getColumn(this.#colDefDeleteAction.colId as string)?.isVisible()
        ) {
            this.gridApi.setColumnsVisible([this.#colDefEditAction.colId!, this.#colDefDeleteAction.colId!], true);
        }
        const voorkeuren = {
            gridModus: this.gridModus()
        };
        await this.#barGvkManager.setItem(this.storageKey(), voorkeuren);
    }

    async #defineWeergave(): Promise<void> {
        const res = await this.#barGvkManager.getItem<{ gridModus: boolean }>(this.storageKey());
        // use weergave from stored object if present
        if (res != null) {
            this.gridModus.set(res.gridModus);
        } else {
            // define if grid modus is default (otherwise list)
            this.gridModus.set(
                this.options().displayModus == null
                    ? this.gridDocumenten().length > this.#rowNumberForGrid()
                    : this.options().displayModus == DidoDocumentenControlDisplayModus.Grid
            );
        }
    }

    #cellCallback(component, params) {
        switch (true) {
            case params.column.colId == DidoDocumentenControlColumn.Zichtbaar:
                return params.value == false
                    ? this.#i18n.get('SHARED.ACTION.EXPORT_EXCEL_NIETZICHTBAAR')
                    : this.#i18n.get('SHARED.ACTION.EXPORT_EXCEL_ZICHTBAAR');
            case params.column.colId == DidoDocumentenControlColumn.Bestandsnaam:
                if (this.intern && params.node.data.documentnummer && params.node.data.safeOri) {
                    let ori = params.node.data.safeOri.changingThisBreaksApplicationSecurity;
                    if (!ori.startsWith('http')) ori = this.#appState.location.release.origin + ori;
                    return '=HYPERLINK("' + ori + '","' + params.value + '")';
                } else return params.value;
            case params.column.colId == DidoDocumentenControlColumn.AangemaaktDatum:
                return this.#datePipe.transform(params.value, 'dd-MM-yyyy');

            default:
                return params.value;
        }
    }

    #defineInterceptionEnabled(enabled: boolean): void {
        this.#layoutLegacy.interception$.next(enabled);
        this.#layout.interception = enabled;
    }

    checkDisableDelete(document: DocumentModel): boolean {
        //als we hier iets wijzigen denk ook aan de knoppen in de  tegelweergave

        let removeAllowed = false;
        if (this.#appState.basiscontext.zone == 'E') {
            removeAllowed = this.options().extDocVerwijderenToegelaten == true;
        }
        if (this.#appState.basiscontext.zone == 'I') {
            if (document.documentnummer) {
                removeAllowed = !document.deleteDisabled && document.actieVersnipperenToelaten && document.isWriteAllowed;
            }
        }

        return this.disabled() || !removeAllowed;
    }
}
